import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FiX, FiUpload, FiTrash2, FiExternalLink, FiMapPin } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { dataParaInput } from '../utils/dateUtils';
import { getImageUrl } from '../utils/imageUtils';

const ViagemModal = ({ viagem, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    destino: '',
    data_ida: '',
    data_volta: '',
    local_embarque: '',
    local_retorno: '',
    horario_embarque: '',
    valor: '',
    roteiro: '',
    vagas: '',
    descricao: '',
    tipo_onibus: 'convencional',
    video_url: '',
    inclusos: '',
    info_embarque: '',
    direitos_deveres: '',
    aviso_importante: '',
    latitude: '',
    longitude: ''
  });
  const [imagens, setImagens] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (viagem) {
      setFormData({
        titulo: viagem.titulo || '',
        destino: viagem.destino || '',
        data_ida: dataParaInput(viagem.data_ida) || '',
        data_volta: dataParaInput(viagem.data_volta) || '',
        local_embarque: viagem.local_embarque || '',
        local_retorno: viagem.local_retorno || '',
        horario_embarque: viagem.horario_embarque || '',
        valor: viagem.valor || '',
        roteiro: viagem.roteiro || '',
        vagas: viagem.vagas || '',
        descricao: viagem.descricao || '',
        tipo_onibus: viagem.tipo_onibus || 'convencional',
        video_url: viagem.video_url || '',
        inclusos: viagem.inclusos || '',
        info_embarque: viagem.info_embarque || '',
        direitos_deveres: viagem.direitos_deveres || '',
        aviso_importante: viagem.aviso_importante || '',
        latitude: viagem.latitude != null ? String(viagem.latitude) : '',
        longitude: viagem.longitude != null ? String(viagem.longitude) : ''
      });
      if (viagem.imagens && Array.isArray(viagem.imagens) && viagem.imagens.length > 0) {
        setImagens(viagem.imagens);
      } else {
        setImagens([]);
      }
    }
  }, [viagem]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const novasImagens = [...imagens, ...files];
    // Limitar a 3 imagens no total
    setImagens(novasImagens.slice(0, 3));
    if (novasImagens.length > 3) {
      toast.error('Máximo de 3 imagens permitidas');
    }
  };

  const handleDeleteImage = async (index, imageUrl) => {
    if (!window.confirm('Tem certeza que deseja excluir esta imagem?')) {
      return;
    }

    // Se for uma imagem existente (string), deletar do servidor
    if (typeof imageUrl === 'string' && viagem) {
      try {
        const encodedUrl = encodeURIComponent(imageUrl);
        await api.delete(`/api/viagens/${viagem.id}/imagens/${encodedUrl}`);
        toast.success('Imagem deletada com sucesso');
        // Remover da lista local
        const novasImagens = imagens.filter((_, i) => i !== index);
        setImagens(novasImagens);
      } catch (error) {
        toast.error('Erro ao deletar imagem');
        console.error('Erro ao deletar imagem:', error);
      }
    } else {
      // Se for um arquivo novo (File), apenas remover da lista
      setImagens(imagens.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      imagens.forEach((img, index) => {
        if (img instanceof File) {
          formDataToSend.append('imagens', img);
        }
      });

      if (viagem) {
        await api.put(`/api/viagens/${viagem.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Viagem atualizada com sucesso');
      } else {
        await api.post('/api/viagens', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Viagem criada com sucesso');
      }
      // Limpar previews de arquivos temporários
      imagens.forEach(img => {
        if (img instanceof File && img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar viagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            {viagem ? 'Editar Viagem' : 'Nova Viagem'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destino *</label>
              <input
                type="text"
                value={formData.destino}
                onChange={(e) => setFormData({ ...formData, destino: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Ida *</label>
              <input
                type="date"
                value={formData.data_ida}
                onChange={(e) => setFormData({ ...formData, data_ida: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Volta</label>
              <input
                type="date"
                value={formData.data_volta}
                onChange={(e) => setFormData({ ...formData, data_volta: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Local de Embarque *</label>
              <input
                type="text"
                value={formData.local_embarque}
                onChange={(e) => setFormData({ ...formData, local_embarque: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Horário de Embarque</label>
              <input
                type="time"
                value={formData.horario_embarque}
                onChange={(e) => setFormData({ ...formData, horario_embarque: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
              <input
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vagas *</label>
              <input
                type="number"
                value={formData.vagas}
                onChange={(e) => setFormData({ ...formData, vagas: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Ônibus</label>
              <select
                value={formData.tipo_onibus}
                onChange={(e) => setFormData({ ...formData, tipo_onibus: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="convencional">Convencional (50 poltronas)</option>
                <option value="tradicional_50">Ônibus Tradicional (50 poltronas)</option>
                <option value="double_decker">Double Decker Semi-Leito (64 poltronas)</option>
                <option value="double_decker_60">Double Decker Semi-Leito (60 poltronas)</option>
                <option value="double_decker_56">Double Decker Semi-Leito (56 poltronas)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Latitude (mapa)</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: -14.235"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Longitude (mapa)</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: -51.925"
              />
            </div>
            <div className="md:col-span-2 -mt-2 p-3 bg-sky-50 border border-sky-200 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                Coordenadas do destino para o mapa público (opcional). No site, o cliente <strong>clica no marcador no mapa</strong> e abre as informações da viagem com link &quot;Ver detalhes&quot;.
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Como obter:</strong> abra o link abaixo, no Google Maps clique com o botão direito no local exato → &quot;O que há aqui?&quot;. A latitude e longitude aparecem embaixo.
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formData.destino || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-sky-700 hover:text-sky-800"
              >
                <FiMapPin size={16} />
                {formData.destino ? `Buscar "${formData.destino}" no Google Maps` : 'Abrir Google Maps para buscar o local'}
                <FiExternalLink size={14} />
              </a>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Roteiro</label>
              <textarea
                value={formData.roteiro}
                onChange={(e) => setFormData({ ...formData, roteiro: e.target.value })}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
              <textarea
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: À vista com desconto, parcelado em até 10x, boleto, cartão de crédito..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">O que está incluso no pacote</label>
              <textarea
                value={formData.inclusos}
                onChange={(e) => setFormData({ ...formData, inclusos: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Ex: Transporte, hospedagem, café da manhã, guia turístico..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Informações Adicionais de Embarque</label>
              <textarea
                value={formData.info_embarque}
                onChange={(e) => setFormData({ ...formData, info_embarque: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Informações sobre horários, documentos necessários, localização do ponto de embarque..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Direitos e Deveres dos Passageiros</label>
              <textarea
                value={formData.direitos_deveres}
                onChange={(e) => setFormData({ ...formData, direitos_deveres: e.target.value })}
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Informações sobre direitos e deveres dos passageiros durante a viagem..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Aviso Importante</label>
              <textarea
                value={formData.aviso_importante}
                onChange={(e) => setFormData({ ...formData, aviso_importante: e.target.value })}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Avisos importantes sobre a viagem, cancelamentos, condições especiais..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">URL do Vídeo</label>
              <input
                type="url"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Imagens (máximo 3)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                disabled={imagens.length >= 3}
              />
              {imagens.length >= 3 && (
                <p className="mt-1 text-sm text-gray-500">Limite de 3 imagens atingido</p>
              )}
              {imagens.length > 0 && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {imagens.slice(0, 3).map((img, displayIndex) => {
                    // Usar o índice real da imagem no array original
                    const realIndex = displayIndex;
                    const getImageSrc = () => {
                      if (img instanceof File) {
                        return URL.createObjectURL(img);
                      }
                      // Se for uma string (URL existente), usar a função utilitária
                      if (typeof img === 'string') {
                        return getImageUrl(img);
                      }
                      return img;
                    };
                    
                    return (
                      <div key={realIndex} className="relative group">
                        <img
                          src={getImageSrc()}
                          alt={`Preview ${realIndex + 1}`}
                          className="w-full h-24 object-cover rounded border border-gray-200"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EErro%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(realIndex, img)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="Excluir imagem"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ViagemModal;
